import { useState, useEffect, useCallback, useRef } from 'react'
import {
  Box, Typography, Alert, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, IconButton, Collapse, Snackbar,
} from '@mui/material'
import {
  CloudUpload, ExpandMore, ExpandLess, CheckCircle,
  HourglassEmpty, ErrorOutline, UploadFile,
} from '@mui/icons-material'
import LoadingState from '../components/common/LoadingState'
import { useApi } from '../hooks/useApi'
import { usePlantContext } from '../hooks/usePlantContext'
import { formatDate, formatRelativeTime } from '../utils/format'

const now = new Date()
const hoursAgo = (h) => new Date(now.getTime() - h * 60 * 60 * 1000).toISOString()
const daysAgo = (d) => hoursAgo(d * 24)

const FALLBACK_ACTIVITY = [
  { id: 'a1', date: hoursAgo(2), source: 'SAP', category: 'Inventory', items: 487, status: 'Success', details: 'Automatic daily inventory pull. 487 items refreshed, 42 quantities changed.' },
  { id: 'a2', date: daysAgo(1), source: 'SAP', category: 'Inventory', items: 487, status: 'Success', details: 'Automatic daily inventory pull. 30 increased, 25 decreased.' },
  { id: 'a3', date: daysAgo(3), source: 'SAP BI', category: 'Master Data', items: 487, status: 'Success', details: '2 new items added (GMI Progresso line). No removals.' },
  { id: 'a4', date: daysAgo(26), source: 'New Horizons', category: 'Forecast', items: 312, status: 'Success', details: 'March forecast upload by H. Allmann. 15 items increased, 8 decreased, 3 new items.' },
  { id: 'a5', date: daysAgo(37), source: 'SAP BI', category: 'Master Data', items: 485, status: 'Success', details: 'Routine master data refresh. No changes.' },
  { id: 'a6', date: daysAgo(56), source: 'New Horizons', category: 'Forecast', items: 310, status: 'Success', details: 'February forecast upload by H. Allmann.' },
  { id: 'a7', date: daysAgo(60), source: 'Manual', category: 'Run Rates', items: 10, status: 'Processed', details: 'Manual upload of updated run rates for Line 4.' },
  { id: 'a8', date: daysAgo(90), source: 'SAP', category: 'Inventory', items: 480, status: 'Error', details: 'Connection timeout during pull. Retried successfully 2h later.' },
]

const STATUS_CONFIG = {
  Success: { color: 'success', icon: CheckCircle },
  Processed: { color: 'info', icon: CheckCircle },
  Pending: { color: 'warning', icon: HourglassEmpty },
  Error: { color: 'error', icon: ErrorOutline },
}

export default function DataHub() {
  const { plant } = usePlantContext()
  const { loading, error, get } = useApi()
  const [activity, setActivity] = useState(FALLBACK_ACTIVITY)
  const [expandedRow, setExpandedRow] = useState(null)
  const [initialLoad, setInitialLoad] = useState(true)
  const [dragActive, setDragActive] = useState(false)
  const [snackbar, setSnackbar] = useState({ open: false, message: '' })
  const fileInputRef = useRef(null)

  useEffect(() => {
    let cancelled = false
    get(`/data/versions?plant=${plant.plantCode}`)
      .then((data) => {
        if (cancelled) return
        if (data?.length) {
          setActivity(data.map((v, i) => ({
            id: v.id || `v-${i}`,
            date: v.uploadedAt || v.createdAt || '',
            source: v.source || 'Unknown',
            category: v.source || 'Data',
            items: v.itemCount || 0,
            status: 'Success',
            details: v.fileName ? `File: ${v.fileName}` : '',
          })))
        }
        setInitialLoad(false)
      })
      .catch(() => {
        if (!cancelled) setInitialLoad(false)
      })
    return () => { cancelled = true }
  }, [plant.plantCode]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleDragOver = useCallback((e) => {
    e.preventDefault()
    setDragActive(true)
  }, [])

  const handleDragLeave = useCallback(() => {
    setDragActive(false)
  }, [])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    setDragActive(false)
    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) {
      setSnackbar({ open: true, message: `Received "${files[0].name}" — processing upload…` })
    }
  }, [])

  const handleFileSelect = useCallback((e) => {
    const files = Array.from(e.target.files)
    if (files.length > 0) {
      setSnackbar({ open: true, message: `Received "${files[0].name}" — processing upload…` })
    }
    e.target.value = ''
  }, [])

  if (initialLoad && loading) return <LoadingState message="Loading data hub…" />

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <CloudUpload color="primary" />
        <Typography variant="h3">Data Hub</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upload source data and monitor integration activity for {plant.name}.
      </Typography>

      {error && (
        <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
          Using demo data — API unavailable ({error})
        </Alert>
      )}

      <Typography variant="h5" sx={{ mb: 1.5 }}>Manual Upload</Typography>
      <Box
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        sx={{
          border: '2px dashed',
          borderColor: dragActive ? 'primary.main' : 'divider',
          borderRadius: 2,
          p: 4,
          mb: 4,
          textAlign: 'center',
          cursor: 'pointer',
          bgcolor: dragActive ? 'primary.light' : 'background.paper',
          transition: 'all 0.2s',
          '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
        }}
      >
        <UploadFile sx={{ fontSize: 40, color: dragActive ? 'primary.main' : 'text.secondary', mb: 1 }} />
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          Drag & drop a file here, or click to browse
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          e.g. New Horizons forecast, run rate updates, inventory snapshots
        </Typography>
        <input
          ref={fileInputRef}
          type="file"
          hidden
          onChange={handleFileSelect}
        />
      </Box>

      <Typography variant="h5" sx={{ mb: 1.5 }}>Integration Activity Log</Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40 }} />
              <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Source</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Items</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activity.map((row) => {
              const isExpanded = expandedRow === row.id
              const statusCfg = STATUS_CONFIG[row.status] || STATUS_CONFIG.Success
              const StatusIcon = statusCfg.icon
              return [
                <TableRow
                  key={row.id}
                  hover
                  sx={{ cursor: 'pointer', '& > td': { borderBottom: isExpanded ? 'none' : undefined } }}
                  onClick={() => setExpandedRow(isExpanded ? null : row.id)}
                >
                  <TableCell>
                    <IconButton size="small">
                      {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">{formatDate(row.date)}</Typography>
                      <Typography variant="caption" color="text.secondary">{formatRelativeTime(row.date)}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{row.source}</TableCell>
                  <TableCell>{row.category}</TableCell>
                  <TableCell align="right">{row.items}</TableCell>
                  <TableCell>
                    <Chip
                      icon={<StatusIcon sx={{ fontSize: 14 }} />}
                      label={row.status}
                      size="small"
                      color={statusCfg.color}
                      variant="outlined"
                    />
                  </TableCell>
                </TableRow>,
                <TableRow key={`${row.id}-detail`}>
                  <TableCell colSpan={6} sx={{ py: 0 }}>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Box sx={{ py: 1.5, px: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          {row.details || 'No additional details available.'}
                        </Typography>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>,
              ]
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        message={snackbar.message}
      />
    </Box>
  )
}
